import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { type ApiError } from "../api/http";
import { downloadBlob } from "../utils/downloadBlob";
import { internalParam, useInternalDataPref } from "./useInternalDataPref";
import type {
  LandingAnalyticsResponse,
  Paginated,
  PlatformAlert,
  PlatformAuditRow,
  PlatformBusinessDetail,
  PlatformBusinessRow,
  PlatformChurnResponse,
  PlatformEngagementResponse,
  PlatformFunnelResponse,
  PlatformHealthResponse,
  PlatformImpersonationRow,
  PlatformModuleAdoptionResponse,
  PlatformOpsResponse,
  PlatformOverviewTrends,
  PlatformOverviewV2,
  PlatformRetentionParams,
  PlatformRetentionResponse,
  PlatformRevenueResponse,
  PlatformSubscriptionRow,
  PlatformUserDetail,
  PlatformUserRow,
  StartImpersonationResponse,
} from "../api/types";

export const platformKeys = {
  overview: (internal: boolean) => ["platform", "overview-v2", internal] as const,
  businesses: (params: Record<string, string>) => ["platform", "businesses", params] as const,
  businessDetail: (id: number) => ["platform", "business", id] as const,
  users: (params: Record<string, string>) => ["platform", "users", params] as const,
  userDetail: (id: number) => ["platform", "user", id] as const,
  subscriptions: ["platform", "subscriptions-v2"] as const,
  funnel: (days: number, internal: boolean) => ["platform", "funnel", days, internal] as const,
  retention: (params: Record<string, string>, internal: boolean) =>
    ["platform", "retention", params, internal] as const,
  engagement: (days: number, internal: boolean) =>
    ["platform", "engagement", days, internal] as const,
  churn: (params: Record<string, string>, internal: boolean) =>
    ["platform", "churn", params, internal] as const,
  modules: (days: number, internal: boolean) => ["platform", "modules", days, internal] as const,
  alerts: (internal: boolean) => ["platform", "alerts", internal] as const,
  health: ["platform", "health"] as const,
  audit: (params: Record<string, string>) => ["platform", "audit", params] as const,
  impersonations: ["platform", "impersonations"] as const,
  revenue: (internal: boolean) => ["platform", "revenue", internal] as const,
  overviewTrends: (days: number, internal: boolean) =>
    ["platform", "overview-trends", days, internal] as const,
  ops: ["platform", "ops"] as const,
  landingAnalytics: (days: number, internal: boolean) =>
    ["platform", "landing-analytics", days, internal] as const,
};

// ── Reads ───────────────────────────────────────────────────────────────────

export function usePlatformOverviewV2() {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformOverviewV2, ApiError>({
    queryKey: platformKeys.overview(includeInternal),
    queryFn: () =>
      api.get<PlatformOverviewV2>(
        `/api/v1/platform/overview/${includeInternal ? "?include_internal=true" : ""}`
      ),
    staleTime: 30 * 1000,
  });
}

export interface PlatformListParams {
  search?: string;
  status?: string;
  plan?: string;
  platform_role?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

function listQs(params: PlatformListParams): Record<string, string> {
  const qs: Record<string, string> = {};
  if (params.search) qs.search = params.search;
  if (params.status) qs.status = params.status;
  if (params.plan) qs.plan = params.plan;
  if (params.platform_role) qs.platform_role = params.platform_role;
  if (params.ordering) qs.ordering = params.ordering;
  if (params.page && params.page > 1) qs.page = String(params.page);
  if (params.page_size) qs.page_size = String(params.page_size);
  return qs;
}

export function usePlatformBusinesses(params: PlatformListParams = {}) {
  const qs = listQs(params);
  const search = new URLSearchParams(qs).toString();
  return useQuery<Paginated<PlatformBusinessRow>, ApiError>({
    queryKey: platformKeys.businesses(qs),
    queryFn: () =>
      api.get<Paginated<PlatformBusinessRow>>(
        `/api/v1/platform/businesses/${search ? `?${search}` : ""}`
      ),
  });
}

export function usePlatformBusinessDetail(id: number | undefined) {
  return useQuery<PlatformBusinessDetail, ApiError>({
    queryKey: platformKeys.businessDetail(id ?? 0),
    queryFn: () => api.get<PlatformBusinessDetail>(`/api/v1/platform/businesses/${id}/`),
    enabled: !!id,
  });
}

export function usePlatformUsers(params: PlatformListParams = {}) {
  const qs = listQs(params);
  const search = new URLSearchParams(qs).toString();
  return useQuery<Paginated<PlatformUserRow>, ApiError>({
    queryKey: platformKeys.users(qs),
    queryFn: () =>
      api.get<Paginated<PlatformUserRow>>(`/api/v1/platform/users/${search ? `?${search}` : ""}`),
  });
}

export function usePlatformUserDetail(id: number | undefined) {
  return useQuery<PlatformUserDetail, ApiError>({
    queryKey: platformKeys.userDetail(id ?? 0),
    queryFn: () => api.get<PlatformUserDetail>(`/api/v1/platform/users/${id}/`),
    enabled: !!id,
  });
}

export function usePlatformSubscriptions(params: PlatformListParams = {}) {
  const qs = listQs(params);
  const search = new URLSearchParams(qs).toString();
  return useQuery<Paginated<PlatformSubscriptionRow>, ApiError>({
    queryKey: ["platform", "subscriptions-v2", qs],
    queryFn: () =>
      api.get<Paginated<PlatformSubscriptionRow>>(
        `/api/v1/platform/subscriptions/${search ? `?${search}` : ""}`
      ),
  });
}

export function usePlatformFunnel(days = 30) {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformFunnelResponse, ApiError>({
    queryKey: platformKeys.funnel(days, includeInternal),
    queryFn: () =>
      api.get<PlatformFunnelResponse>(
        `/api/v1/platform/funnel/?days=${days}${internalParam(includeInternal)}`
      ),
  });
}

export function usePlatformRetention(params: PlatformRetentionParams & { weeks?: number } = {}) {
  const { includeInternal } = useInternalDataPref();
  const qs: Record<string, string> = {};
  if (params.granularity) qs.granularity = params.granularity;
  if (params.entity) qs.entity = params.entity;
  if (params.activity) qs.activity = params.activity;
  if (params.granularity === "month") {
    if (params.periods) qs.periods = String(params.periods);
  } else if (params.periods ?? params.weeks) {
    qs.weeks = String(params.periods ?? params.weeks);
  }
  const search = new URLSearchParams(qs).toString();
  return useQuery<PlatformRetentionResponse, ApiError>({
    queryKey: platformKeys.retention(qs, includeInternal),
    queryFn: () =>
      api.get<PlatformRetentionResponse>(
        `/api/v1/platform/retention/${search ? `?${search}` : ""}${
          includeInternal ? `${search ? "&" : "?"}include_internal=true` : ""
        }`
      ),
  });
}

export function usePlatformEngagement(days = 90) {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformEngagementResponse, ApiError>({
    queryKey: platformKeys.engagement(days, includeInternal),
    queryFn: () =>
      api.get<PlatformEngagementResponse>(
        `/api/v1/platform/engagement/?days=${days}${internalParam(includeInternal)}`
      ),
    staleTime: 30 * 1000,
  });
}

export function usePlatformChurn(
  params: { period?: "week" | "month"; entity?: "user" | "business" } = {}
) {
  const { includeInternal } = useInternalDataPref();
  const qs: Record<string, string> = {};
  if (params.period) qs.period = params.period;
  if (params.entity) qs.entity = params.entity;
  const search = new URLSearchParams(qs).toString();
  return useQuery<PlatformChurnResponse, ApiError>({
    queryKey: platformKeys.churn(qs, includeInternal),
    queryFn: () =>
      api.get<PlatformChurnResponse>(
        `/api/v1/platform/churn/${search ? `?${search}` : ""}${
          includeInternal ? `${search ? "&" : "?"}include_internal=true` : ""
        }`
      ),
    staleTime: 30 * 1000,
  });
}

export function usePlatformModules(days = 30) {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformModuleAdoptionResponse, ApiError>({
    queryKey: platformKeys.modules(days, includeInternal),
    queryFn: () =>
      api.get<PlatformModuleAdoptionResponse>(
        `/api/v1/platform/module-adoption/?days=${days}${internalParam(includeInternal)}`
      ),
  });
}

export function usePlatformAlerts() {
  const { includeInternal } = useInternalDataPref();
  return useQuery<{ alerts: PlatformAlert[] }, ApiError>({
    queryKey: platformKeys.alerts(includeInternal),
    queryFn: () =>
      api.get<{ alerts: PlatformAlert[] }>(
        `/api/v1/platform/alerts/${includeInternal ? "?include_internal=true" : ""}`
      ),
  });
}

export function usePlatformHealth() {
  return useQuery<PlatformHealthResponse, ApiError>({
    queryKey: platformKeys.health,
    queryFn: () => api.get<PlatformHealthResponse>("/api/v1/platform/health/"),
  });
}

export function usePlatformAudit(
  params: {
    action?: string;
    actor?: string;
    ordering?: string;
    created_after?: string;
    created_before?: string;
    page?: number;
  } = {}
) {
  const qs: Record<string, string> = {};
  if (params.action) qs.action = params.action;
  if (params.actor) qs.actor = params.actor;
  if (params.ordering) qs.ordering = params.ordering;
  if (params.created_after) qs.created_after = params.created_after;
  if (params.created_before) qs.created_before = params.created_before;
  if (params.page && params.page > 1) qs.page = String(params.page);
  const search = new URLSearchParams(qs).toString();
  return useQuery<Paginated<PlatformAuditRow>, ApiError>({
    queryKey: platformKeys.audit(qs),
    queryFn: () =>
      api.get<Paginated<PlatformAuditRow>>(`/api/v1/platform/audit/${search ? `?${search}` : ""}`),
  });
}

export function usePlatformRevenue() {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformRevenueResponse, ApiError>({
    queryKey: platformKeys.revenue(includeInternal),
    queryFn: () =>
      api.get<PlatformRevenueResponse>(
        `/api/v1/platform/revenue/${includeInternal ? "?include_internal=true" : ""}`
      ),
    staleTime: 30 * 1000,
  });
}

export function usePlatformOverviewTrends(days = 30) {
  const { includeInternal } = useInternalDataPref();
  return useQuery<PlatformOverviewTrends, ApiError>({
    queryKey: platformKeys.overviewTrends(days, includeInternal),
    queryFn: () =>
      api.get<PlatformOverviewTrends>(
        `/api/v1/platform/overview-trends/?days=${days}${internalParam(includeInternal)}`
      ),
    staleTime: 30 * 1000,
  });
}

export function usePlatformOps() {
  return useQuery<PlatformOpsResponse, ApiError>({
    queryKey: platformKeys.ops,
    queryFn: () => api.get<PlatformOpsResponse>("/api/v1/platform/ops/"),
  });
}

export function usePlatformLandingAnalytics(days: number) {
  const { includeInternal } = useInternalDataPref();
  return useQuery<LandingAnalyticsResponse, ApiError>({
    queryKey: platformKeys.landingAnalytics(days, includeInternal),
    queryFn: () =>
      api.get<LandingAnalyticsResponse>(
        `/api/v1/platform/landing-analytics/?days=${days}${internalParam(includeInternal)}`
      ),
    staleTime: 30 * 1000,
  });
}

/** CSV export of the daily landing metrics for the current window. */
export function useExportLandingAnalytics() {
  const { includeInternal } = useInternalDataPref();
  return useMutation<void, Error, { days: number }>({
    mutationFn: ({ days }) =>
      downloadBlob({
        endpoint: `/api/v1/platform/landing-analytics/export/`,
        filename: "landing-analytics.csv",
        queryParams: {
          days: String(days),
          ...(includeInternal ? { include_internal: "true" } : {}),
        },
      }),
  });
}

export type PlatformExportDataset = "businesses" | "users" | "subscriptions" | "audit";

/** Server-side CSV export — streams ALL rows matching the current filters
 * (not just the visible page) via the authenticated download helper. */
export function useExportCsv() {
  return useMutation<void, Error, { dataset: PlatformExportDataset; params?: Record<string, string> }>({
    mutationFn: ({ dataset, params }) =>
      downloadBlob({
        endpoint: `/api/v1/platform/${dataset}/export/`,
        filename: `platform-${dataset}.csv`,
        queryParams: params,
      }),
  });
}

export function usePlatformImpersonations() {
  return useQuery<{ results: PlatformImpersonationRow[] }, ApiError>({
    queryKey: platformKeys.impersonations,
    queryFn: () => api.get("/api/v1/platform/impersonations/"),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export function useSuspendBusiness() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/api/v1/platform/businesses/${id}/suspend/`, { reason }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: platformKeys.businessDetail(vars.id) });
      qc.invalidateQueries({ queryKey: ["platform", "businesses"] });
      qc.invalidateQueries({ queryKey: ["platform", "alerts"] });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
      qc.invalidateQueries({ queryKey: ["platform", "overview-v2"] });
    },
  });
}

export function useReactivateBusiness() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) => api.post(`/api/v1/platform/businesses/${id}/reactivate/`, { reason: reason ?? "" }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: platformKeys.businessDetail(vars.id) });
      qc.invalidateQueries({ queryKey: ["platform", "businesses"] });
      qc.invalidateQueries({ queryKey: ["platform", "alerts"] });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
      qc.invalidateQueries({ queryKey: ["platform", "overview-v2"] });
    },
  });
}

export function useReonboardBusiness() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) => api.post(`/api/v1/platform/businesses/${id}/reonboard/`, { reason: reason ?? "" }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: platformKeys.businessDetail(vars.id) });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}

export function usePasswordReset() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; email_sent: boolean }, ApiError, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) => api.post(`/api/v1/platform/users/${id}/password-reset/`, { reason: reason ?? "" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "audit"] }),
  });
}

export function useResendVerification() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) => api.post(`/api/v1/platform/users/${id}/resend-verification/`, { reason: reason ?? "" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "audit"] }),
  });
}

export function useCompSubscription() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; months: number; reason: string }>({
    mutationFn: ({ id, months, reason }) =>
      api.post(`/api/v1/platform/subscriptions/${id}/comp/`, { months, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformKeys.subscriptions });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      api.post(`/api/v1/platform/subscriptions/${id}/cancel/`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformKeys.subscriptions });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}

export function useExtendTrial() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; days: number; reason?: string }>({
    mutationFn: ({ id, days, reason }) =>
      api.post(`/api/v1/platform/subscriptions/${id}/extend-trial/`, { days, reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformKeys.subscriptions });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}

export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { id: number; plan: string; reason: string }>({
    mutationFn: ({ id, plan, reason }) =>
      api.post(`/api/v1/platform/subscriptions/${id}/change-plan/`, { plan, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformKeys.subscriptions });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}

export function useStartImpersonation() {
  return useMutation<StartImpersonationResponse, ApiError, { target_user_id: number; reason: string }>({
    mutationFn: (body) => api.post("/api/v1/platform/impersonate/", body),
  });
}

export function useEndImpersonation() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { impersonation_id: number }>({
    mutationFn: ({ impersonation_id }) => api.post(`/api/v1/platform/impersonate/${impersonation_id}/end/`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformKeys.impersonations });
      qc.invalidateQueries({ queryKey: ["platform", "audit"] });
    },
  });
}
